import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router';
import type { SessionPayload } from '@wr/shared';
import { ApiError, errorMessage, post } from '../api.js';
import { Field, Notice } from '../components/ui.js';
import { rememberPasswordCheck } from '../offline/kioskPin.js';
import { useLoaded } from '../session.js';
import { AuthLayout } from './AuthLayout.js';

export function SignIn() {
  const { rules, setSession } = useLoaded();
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await post<SessionPayload>('/auth/sign-in', { email, password });
      // For leaving Hand the tablet or the kiosk without network and without a PIN.
      rememberPasswordCheck(email, password);
      setSession(session);
    } catch (err) {
      setPassword('');
      setError(
        err instanceof ApiError && err.code === 'invalid_credentials'
          ? `The email or password is incorrect, or the account is locked. After ${rules.lockout.maxFailedAttempts} failed attempts, the account is locked for ${rules.lockout.lockoutMinutes} minutes.`
          : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Sign in">
      {notice && <Notice kind="ok">{notice}</Notice>}
      {error && <Notice kind="error">{error}</Notice>}
      <form onSubmit={(e) => void submit(e)} className="vstack">
        <Field
          label="Email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button type="submit" className="btn primary block" disabled={busy}>
          Sign in
        </button>
      </form>
      <p className="small">
        <Link to="/forgot-password">Forgot your password?</Link>
      </p>
    </AuthLayout>
  );
}
